// /pdks "Eşleşmeyen damgalar": güveni kaldırılmış bir cihazın çevrimdışı
// kuyruğundan gelen ve sunucunun otomatik işlemediği damgalar (ADR-019).
//
// Kabul, damgadan bir manager_entry kaydı yaratır; reddetme kayıt yaratmaz. İkisi
// de geri alınamaz, bu yüzden ikisi de onay ister. Liste uç noktası okuma
// olduğu halde time_attendance.edit ister (routes/time-attendance.ts gerekçesi:
// konum ve cihaz verisi yalnız işlem yapabilen kişiye gösterilir); çağıran bu
// hücreyi taşımıyorsa bölüm hiç çizilmez. Ham enlem/boylam gösterilmez: karar
// için sunucunun geofence hükmü yeterli ve koordinat gereksiz kişisel veridir.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  Badge,
  Banner,
  Button,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  type BadgeTone,
  type DataTableColumn,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { useTimeZone } from "@/api/use-time-zone";
import { PersonLink } from "@/components/person-link";
import { useEmployeeNames } from "@/features/employees/use-employee-names";

import { formatTimeInstant } from "./format-instant";
import {
  decideOrphanedPunch,
  fetchOrphanedPunchesPage,
  type OrphanedPunchRow,
} from "./time-api";

const ORPHANED_QUERY_KEY = ["time", "orphaned-punches"] as const;

const GEOFENCE_STATE_TONE: Readonly<Record<string, BadgeTone>> = {
  inside: "success",
  outside: "danger",
  indeterminate: "neutral",
};

type Decision = "accept" | "dismiss";

interface PendingDecision {
  readonly decision: Decision;
  readonly row: OrphanedPunchRow;
}

export function OrphanedPunchesSection(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const queryClient = useQueryClient();
  const names = useEmployeeNames();
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const orphans = useKeysetQuery<OrphanedPunchRow>({
    queryKey: ORPHANED_QUERY_KEY,
    fetchPage: fetchOrphanedPunchesPage,
  });

  const decide = useMutation({
    mutationFn: ({ decision, row }: PendingDecision) =>
      decideOrphanedPunch(row.orphaned_punch_id, decision),
    onSuccess: (_row, { decision }) => {
      setPending(null);
      setNotice(
        decision === "accept" ? t("time.pdks.orphans.accepted") : t("time.pdks.orphans.dismissed"),
      );
      void queryClient.invalidateQueries({ queryKey: ORPHANED_QUERY_KEY });
      // An accepted punch becomes a submitted entry in the feed and review queue.
      if (decision === "accept") void queryClient.invalidateQueries({ queryKey: ["time", "entries"] });
    },
    onError: () => {
      // A 409 means someone already decided this row; the list must show that.
      void queryClient.invalidateQueries({ queryKey: ORPHANED_QUERY_KEY });
    },
  });

  const columns: readonly DataTableColumn<OrphanedPunchRow>[] = [
    {
      id: "employee",
      header: t("time.column.employee"),
      cell: (row): React.ReactNode => {
        const name = names.get(row.employee_id);
        return (
          <PersonLink employeeId={row.employee_id}>
            {name === undefined ? (
              <span className="font-mono text-[12px] text-[var(--fg-secondary)]">
                {row.employee_id.slice(0, 8)}
              </span>
            ) : (
              <span>{name}</span>
            )}
          </PersonLink>
        );
      },
    },
    {
      id: "claimed_at",
      header: t("time.pdks.orphans.column.claimed_at"),
      cell: (row): string => formatTimeInstant(i18n.language, timeZone, row.claimed_at),
    },
    {
      id: "kind",
      header: t("time.pdks.orphans.column.kind"),
      cell: (row): string => t(`time.pdks.orphans.kind.${row.kind}`),
    },
    {
      id: "geofence",
      header: t("time.pdks.orphans.column.geofence"),
      cell: (row): React.ReactNode =>
        row.geofence_state === null ? (
          <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>
        ) : (
          <Badge tone={GEOFENCE_STATE_TONE[row.geofence_state] ?? "neutral"}>
            {t(`time.pdks.orphans.geofence.${row.geofence_state}`)}
          </Badge>
        ),
    },
    {
      id: "reason",
      header: t("time.pdks.orphans.column.reason"),
      cell: (row): string =>
        t(`time.pdks.orphans.reason.${row.refused_reason}`),
    },
    {
      id: "actions",
      header: t("time.column.actions"),
      align: "end",
      cell: (row): React.ReactNode => (
        <div className="flex justify-end gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={decide.isPending}
            onClick={(): void => {
              decide.reset();
              setPending({ decision: "accept", row });
            }}
          >
            {t("time.pdks.orphans.accept")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={decide.isPending}
            onClick={(): void => {
              decide.reset();
              setPending({ decision: "dismiss", row });
            }}
          >
            {t("time.pdks.orphans.dismiss")}
          </Button>
        </div>
      ),
    },
  ];

  const close = (): void => {
    if (decide.isPending) return;
    setPending(null);
  };

  return (
    <section aria-labelledby="time-orphaned-punches-heading" className="space-y-3">
      <div>
        <h2
          id="time-orphaned-punches-heading"
          className="font-serif text-[20px] text-[var(--fg-primary)]"
        >
          {t("time.pdks.orphans.title")}
        </h2>
        <p className="mt-1 font-sans text-[14px] leading-relaxed text-[var(--fg-secondary)]">
          {t("time.pdks.orphans.description")}
        </p>
      </div>

      {notice !== null ? (
        <Banner severity="success" title={notice} onDismiss={(): void => setNotice(null)} />
      ) : null}

      {orphans.isLoading ? (
        <Loading label={t("time.pdks.orphans.loading")} rows={3} />
      ) : orphans.isError ? (
        <ErrorState
          title={t("time.pdks.orphans.error_title")}
          description={errorMessage(orphans.error, t)}
        />
      ) : orphans.items.length > 0 ? (
        <DataTable<OrphanedPunchRow>
          caption={t("time.pdks.orphans.table_caption")}
          columns={columns}
          rows={orphans.items}
          rowKey={(row): string => row.orphaned_punch_id}
          hasNextPage={orphans.hasNextPage}
          onLoadMore={orphans.fetchNextPage}
          isFetchingNextPage={orphans.isFetchingNextPage}
          loadMoreLabel={t("time.load_more")}
        />
      ) : (
        <Empty
          title={t("time.pdks.orphans.empty_title")}
          description={t("time.pdks.orphans.empty_description")}
        />
      )}

      <Modal
        open={pending !== null}
        onClose={close}
        closeOnEscape={!decide.isPending}
        closeOnScrimClick={!decide.isPending}
      >
        <ModalHeader
          title={
            pending?.decision === "accept"
              ? t("time.pdks.orphans.confirm.accept_title")
              : t("time.pdks.orphans.confirm.dismiss_title")
          }
        />
        <ModalBody>
          <p className="font-sans text-[14px] leading-relaxed text-[var(--fg-secondary)]">
            {pending?.decision === "accept"
              ? t("time.pdks.orphans.confirm.accept_body")
              : t("time.pdks.orphans.confirm.dismiss_body")}
          </p>
          {decide.error != null ? (
            <p role="alert" className="mt-3 text-[13px] text-[var(--state-danger)]">
              {errorMessage(decide.error, t)}
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={close} disabled={decide.isPending}>
            {t("time.pdks.confirm.cancel")}
          </Button>
          <Button
            variant={pending?.decision === "dismiss" ? "danger" : "primary"}
            size="sm"
            disabled={pending === null || decide.isPending}
            onClick={(): void => {
              if (pending !== null) decide.mutate(pending);
            }}
          >
            {pending?.decision === "accept"
              ? t("time.pdks.orphans.confirm.accept_confirm")
              : t("time.pdks.orphans.confirm.dismiss_confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </section>
  );
}
